import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

const inFlight = new Map();
const getPage = (query) => {
  const key = JSON.stringify(query);
  if (inFlight.has(key)) return inFlight.get(key);
  const request = api.get('/api/sports/appointments/paged', { params: query }).then((res) => {
    if (res?.isSuccess === false) throw new Error(res.resultMessage || 'Randevular alınamadı.');
    const data = res?.data || res;
    if (!Array.isArray(data?.items) || !Number.isFinite(data.totalCount) || !data.counts) {
      throw new Error('Randevu listesi alınamadı.');
    }
    return data;
  }).finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};

export default function useSportsAppointmentList({ tab, search, sort, enabled }) {
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [query, setQuery] = useState({ page: 1, pageSize: 15, tab, search: search.trim(), sort });
  const [data, setData] = useState({ items: [], totalCount: 0, counts: { all: 0, confirmed: 0, paymentFailed: 0, cancelled: 0, refunded: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const busy = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 1000);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setQuery((previous) => previous.tab === tab && previous.search === debouncedSearch.trim() && previous.sort === sort
      ? previous : { ...previous, page: 1, tab, search: debouncedSearch.trim(), sort });
  }, [tab, debouncedSearch, sort]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const id = ++requestId.current;
    busy.current = true;
    setLoading(true);
    setError(null);
    try {
      const result = await getPage(query);
      if (id !== requestId.current) return;
      const last = Math.max(1, Math.ceil(result.totalCount / query.pageSize));
      if (query.page > last) { setQuery((current) => ({ ...current, page: last })); return; }
      setData(result);
    } catch (err) {
      if (id === requestId.current) {
        setError(err.message || 'Randevular yüklenemedi.');
        setData((previous) => ({ ...previous, items: [], totalCount: 0 }));
      }
    } finally {
      if (id === requestId.current) { busy.current = false; setLoading(false); }
    }
  }, [enabled, query]);

  useEffect(() => {
    refresh();
    const timer = enabled ? setInterval(() => {
      if (!busy.current && document.visibilityState !== 'hidden') refresh();
    }, 20000) : null;
    return () => { requestId.current += 1; clearInterval(timer); };
  }, [enabled, refresh]);

  return { ...data, loading, error, refresh, page: query.page,
    totalPages: Math.ceil(data.totalCount / query.pageSize),
    setPage: (next) => setQuery((current) => ({ ...current, page: Math.max(1, typeof next === 'function' ? next(current.page) : next) })) };
}
